import * as React from 'react';
import { ReactNode, useContext, useEffect, useState, MouseEvent } from 'react';
import { AdminDataWorkDTO, AdminDataWorkOpenAccessFundedDTO, ExternalIdDTO, SubjectDTO, WorkDescriptionDTO, WorkDTO, WorkKeywordDTO } from '../../../api/core';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import { Layout } from '../../Layout';
import { IntlShape, useIntl } from 'react-intl';
import { FormContent } from '../FormContent';
import { Block } from '../components/layout/Block';
import { getVocabularyOptions } from '../../../Vocabulary/util';
import { EnrichedValues, PubtypeGroup, WorkFormPage } from '../../../util/types';
import { PubtypeUtils } from '../../../util/pubtype-utils';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { pageComponents, WorkPageComponent } from '../../../Settings/work-page-components-mapping';
import { BasicPage } from './pages/BasicPage';
import { SpecificPage } from './pages/SpecificPage';
import { IdsPage } from './pages/IdsPage';
import { PersonsPage } from './pages/PersonsPage';
import { IsPartOfPage } from './pages/IsPartOfPage';
import { HasPartPage } from './pages/HasPartPage';
import { OrganisationsPage } from './pages/OrganisationsPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { EventsPage } from './pages/EventsPage';
import { ContentPage } from './pages/ContentPage';
import { AdministrativePage } from './pages/AdministrativePage';
import { Props } from './pages/props';
import { validate } from '../../../util/validation/validate';
import { validationWorkEntityMapping } from '../../../Settings/validationWorkEntityMapping';
import { LabelledDropdown } from '../../components/form/LabelledDropdown';
import { Validation } from '../../../util/validation/interfaces';
import { InternalLink } from '../../components/InternalLink';
import { StateContext, StateContextType } from '../../../util/providers/StateProvider';
import { ExternalLink } from '../../components/ExternalLink';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ImportServiceApi } from 'api/import/apis/ImportServiceApi';
import { getAuthMiddleware } from 'Auth/utils/GetAuthMiddleware';
import { ToastContext, ToastContextType } from 'util/providers/ToastProvider';

const REVIEW_EXIDS = new Set(['scopus', 'wos']);
const REVIEW_TYPES = new Set(['article_overview', 'article_journal']);

export interface EditProps {
  modifiedModel: WorkDTO;
  enrichedValues: EnrichedValues;
  page: WorkFormPage;
  setPage: (page: WorkFormPage) => void;
  submit: (edit?: { peerReviewed?: string }) => void;
  updateField: (field: keyof WorkDTO, value: any) => void;
  updateAdminField: (field: keyof AdminDataWorkDTO, value: any) => void;
  saving: boolean;
  setModifiedModel: React.Dispatch<React.SetStateAction<WorkDTO>>;
  setEnrichedValues: React.Dispatch<React.SetStateAction<EnrichedValues>>;
}

export function FormWork(props: EditProps): ReactNode {
  const intl: IntlShape = useIntl();
  const toast: ToastContextType = useContext(ToastContext);
  const {authorizer, session, actions} = useContext(AuthContext);
  const state: StateContextType = useContext(StateContext);
  const [showPeerReviewedDialog, setShowPeerReviewedDialog] = useState(false);
  const importApi: ImportServiceApi = new ImportServiceApi().withMiddleware(getAuthMiddleware(actions));
  const [loading, setLoading] = useState(false);

  const validation: Validation = !props.modifiedModel.pubtype ? {
    valid: false,
    root: {valid: false, fields: {}},
    admin: {valid: false, fields: {}},
  } : validate(props.modifiedModel, props.modifiedModel.adminDataWork, 'work', session, validationWorkEntityMapping[props.modifiedModel.pubtype]);

  const [loadedPage, setLoadedPage] = React.useState<string>(null);
  useEffect(() => {
    setLoadedPage(props.page);
  }, [props.page]);

  const fieldsByPubtype: string[] = PubtypeUtils.getFieldsByPubtype(props.modifiedModel.pubtype);

  const pagesWithComponents = fieldsByPubtype.length < 1 ? [] : Object.entries(pageComponents)
    .map(([page, components]: [string, WorkPageComponent[]]): { page: string; components: WorkPageComponent[] } => {
      return {
        page,
        components: components.filter((component: WorkPageComponent): boolean => fieldsByPubtype.includes(component.key)),
      };
    }).filter((p: { page: string; components: WorkPageComponent[] }): boolean => p.components.length > 0);

  const currentIndex = pagesWithComponents.findIndex((p) => p.page === props.page);
  const nextPage: string = currentIndex + 1 < pagesWithComponents.length ? pagesWithComponents[currentIndex + 1]?.page : undefined;
  const prevPage: string = currentIndex > 0 ? pagesWithComponents[currentIndex - 1]?.page : undefined;

  const hasActiveEnrichment = (page: WorkFormPage): boolean => {
    switch (page) {
      case 'content': {
        const enrichedKeywords =
          props.enrichedValues?.workKeywords as WorkKeywordDTO[] | undefined;

        const enrichedSubjects =
          props.enrichedValues?.workSubjects as SubjectDTO[] | undefined;

        const enrichedDescriptions =
          props.enrichedValues?.workDescriptions as WorkDescriptionDTO[] | undefined;

        const hasKeyword = enrichedKeywords?.some(
          (enrichedKeyword: WorkKeywordDTO): boolean =>
            (props.modifiedModel.workKeywords || []).some(
              (keyword: WorkKeywordDTO): boolean =>
                keyword.keyword === enrichedKeyword.keyword
            )
        ) ?? false;

        const hasSubject = enrichedSubjects?.some(
          (enrichedSubject: SubjectDTO): boolean =>
            (props.modifiedModel.workSubjects || []).some(
              (subject: SubjectDTO): boolean =>
                subject.subjectId === enrichedSubject.subjectId &&
                subject.type === enrichedSubject.type
            )
        ) ?? false;

        const hasDescription = enrichedDescriptions?.some(
          (enrichedDescription: WorkDescriptionDTO): boolean =>
            (props.modifiedModel.workDescriptions || []).some(
              (description: WorkDescriptionDTO): boolean =>
                description.content === enrichedDescription.content &&
                description.descriptionType === enrichedDescription.descriptionType &&
                description.language === enrichedDescription.language
            )
        ) ?? false;

        return hasKeyword || hasSubject || hasDescription;
      }

      case 'ids': {
        const enrichedExternalIds =
          props.enrichedValues?.externalIds as ExternalIdDTO[] | undefined;

        return enrichedExternalIds?.some(
          (enrichedExternalId: ExternalIdDTO): boolean =>
            (props.modifiedModel.externalIds || []).some(
              (externalId: ExternalIdDTO): boolean =>
                externalId.externalId === enrichedExternalId.externalId &&
                externalId.type === enrichedExternalId.type
            )
        ) ?? false;
      }

      case 'administrative': {
        const enrichedAdminDataWork = (
          props.enrichedValues?.adminDataWork as AdminDataWorkDTO[] | undefined
        )?.[0];

        if (!enrichedAdminDataWork) {
          return false;
        }

        const currentAdminDataWork = props.modifiedModel.adminDataWork;

        const hasLicense =
          enrichedAdminDataWork.license !== undefined &&
          enrichedAdminDataWork.license === currentAdminDataWork.license;

        const hasAccessLevel =
          enrichedAdminDataWork.adminDataWorkOpenAccess?.accessLevel !== undefined &&
          enrichedAdminDataWork.adminDataWorkOpenAccess.accessLevel ===
            currentAdminDataWork.adminDataWorkOpenAccess?.accessLevel;

        const hasArticleProcessingCharge =
          enrichedAdminDataWork.adminDataWorkOpenAccess?.articleProcessingCharge !== undefined &&
          enrichedAdminDataWork.adminDataWorkOpenAccess.articleProcessingCharge ===
            currentAdminDataWork.adminDataWorkOpenAccess?.articleProcessingCharge;

        const hasFunded =
          (
            enrichedAdminDataWork
              .adminDataWorkOpenAccess
              ?.adminDataWorkOpenAccessFundeds || []
          ).some(
            (enrichedFund: AdminDataWorkOpenAccessFundedDTO): boolean =>
              (
                currentAdminDataWork
                  .adminDataWorkOpenAccess
                  ?.adminDataWorkOpenAccessFundeds || []
              ).some(
                (currentFund: AdminDataWorkOpenAccessFundedDTO): boolean =>
                  currentFund.affiliation === enrichedFund.affiliation &&
                  currentFund.type === enrichedFund.type
              )
          );

        return hasLicense || hasAccessLevel || hasArticleProcessingCharge || hasFunded;
      }

      default:
        return false;
    }
  };

  const menu: MenuItem[] = pagesWithComponents.map((workFormPage) => {
    const classNames: string[] = [];
    if (props.page === workFormPage.page) classNames.push('active bg-primary');
    if (!validation.pages[workFormPage.page]) classNames.push('invalid');
    if (hasActiveEnrichment(workFormPage.page)) classNames.push('enriched');
    return {
      label: intl.formatMessage({id: `work.header.${workFormPage.page}`}),
      disabled: !props.modifiedModel.pubtype || !pagesWithComponents.some(pwc => pwc.page === workFormPage.page),
      className: classNames.join(' '),
      icon: validation.pages[workFormPage.page] ? 'pi pi-check' : 'pi pi-exclamation-triangle',
      command: (): void => props.setPage(workFormPage.page),
    };
  });

  const onSubmit = (): void => {
    if (
      REVIEW_TYPES.has(props.modifiedModel.pubtype) &&
      ((props.modifiedModel.externalIds || [])).some((externalId: ExternalIdDTO): boolean =>
        REVIEW_EXIDS.has(externalId.type)
      ) &&
      props.modifiedModel.peerReviewed === 'no_value'
    ) {
      return setShowPeerReviewedDialog(true);
    }
    props.submit();
  };

  const getPage = (): React.ReactElement => {
    const pubtypeGroup: PubtypeGroup = PubtypeUtils.getGroupByPubtype(props.modifiedModel.pubtype);

    const pageProps: Props = {
      setField: props.updateField,
      setAdminField: props.updateAdminField,
      validation,
      modifiedModel: props.modifiedModel,
      enrichedValues: props.enrichedValues,
      permissionEntity: `work_${pubtypeGroup.toLowerCase().replaceAll('_', '-')}`,
      pubtypeGroup,
      autoFocus: loadedPage === props.page,
    };
    switch (props.page) {
      case 'specific':
        return <SpecificPage {...pageProps} />;
      case 'ids':
        return <IdsPage {...pageProps} />;
      case 'persons':
        return <PersonsPage {...pageProps} />;
      case 'ispartof':
        return <IsPartOfPage {...pageProps} />;
      case 'haspart':
        return <HasPartPage {...pageProps} />;
      case 'organisations':
        return <OrganisationsPage {...pageProps} />;
      case 'projects':
        return <ProjectsPage {...pageProps} />;
      case 'events':
        return <EventsPage {...pageProps} />;
      case 'content':
        return <ContentPage {...pageProps} />;
      case 'administrative':
        return <AdministrativePage {...pageProps} />;
      default:
      case 'basic':
        return <BasicPage {...pageProps} />;
    }
  };


  const getAddedValues = <T,>(
    oldValues: T[] = [],
    newValues: T[] = [],
    equals: (a: T, b: T) => boolean
  ): T[] => {
    return newValues.filter(
      (newValue: T) =>
        !oldValues.some(
          (oldValue: T) => equals(oldValue, newValue)
        )
    );
  };

  const hasEnrichmentChanges = (values: EnrichedValues): boolean => {
    const adminDataWork = (values.adminDataWork as AdminDataWorkDTO[] | undefined)?.[0];

    return (
      ((values.workKeywords as WorkKeywordDTO[] | undefined)?.length ?? 0) > 0 ||
      ((values.workSubjects as SubjectDTO[] | undefined)?.length ?? 0) > 0 ||
      ((values.externalIds as ExternalIdDTO[] | undefined)?.length ?? 0) > 0 ||
      ((values.workDescriptions as WorkDescriptionDTO[] | undefined)?.length ?? 0) > 0 ||
      adminDataWork?.license !== undefined ||
      adminDataWork?.adminDataWorkOpenAccess?.accessLevel !== undefined ||
      adminDataWork?.adminDataWorkOpenAccess?.articleProcessingCharge !== undefined ||
      ((adminDataWork?.adminDataWorkOpenAccess?.adminDataWorkOpenAccessFundeds?.length ?? 0) > 0)
    );
  };

  const startWorkEnrichment = (event: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>): void => {
    event.preventDefault();
    setLoading(true);

    if(props.modifiedModel.id) {
      importApi.startManualMmsEnrichmentJob({ workDTO: props.modifiedModel })
        .then((result: WorkDTO) => {
          const enrichedValues: EnrichedValues = {
            workKeywords: getAddedValues(
              props.modifiedModel.workKeywords || [],
              result.workKeywords || [],
              (oldValue: WorkKeywordDTO, newValue: WorkKeywordDTO): boolean =>
                oldValue.keyword === newValue.keyword
            ),

            workSubjects: getAddedValues(
              props.modifiedModel.workSubjects || [],
              result.workSubjects || [],
              (oldValue: SubjectDTO, newValue: SubjectDTO): boolean =>
                oldValue.subjectId === newValue.subjectId &&
                oldValue.type === newValue.type
            ),

            externalIds: getAddedValues(
              props.modifiedModel.externalIds || [],
              result.externalIds || [],
              (oldValue: ExternalIdDTO, newValue: ExternalIdDTO): boolean =>
                oldValue.externalId === newValue.externalId &&
                oldValue.type === newValue.type
            ),

            workDescriptions: getAddedValues(
              props.modifiedModel.workDescriptions || [],
              result.workDescriptions || [],
              (oldValue: WorkDescriptionDTO, newValue: WorkDescriptionDTO): boolean =>
                oldValue.content === newValue.content &&
                oldValue.descriptionType === newValue.descriptionType &&
                oldValue.language === newValue.language
            ),

            adminDataWork: [{
              license:
                props.modifiedModel.adminDataWork.license !== result.adminDataWork.license
                  ? result.adminDataWork.license
                  : undefined,

              adminDataWorkOpenAccess: {
                accessLevel:
                  props.modifiedModel.adminDataWork.adminDataWorkOpenAccess?.accessLevel !==
                  result.adminDataWork.adminDataWorkOpenAccess?.accessLevel
                    ? result.adminDataWork.adminDataWorkOpenAccess?.accessLevel
                    : undefined,

                articleProcessingCharge:
                  props.modifiedModel.adminDataWork.adminDataWorkOpenAccess?.articleProcessingCharge !==
                  result.adminDataWork.adminDataWorkOpenAccess?.articleProcessingCharge
                    ? result.adminDataWork.adminDataWorkOpenAccess?.articleProcessingCharge
                    : undefined,

                adminDataWorkOpenAccessFundeds: getAddedValues(
                  props.modifiedModel.adminDataWork.adminDataWorkOpenAccess?.adminDataWorkOpenAccessFundeds || [],
                  result.adminDataWork.adminDataWorkOpenAccess?.adminDataWorkOpenAccessFundeds || [],
                  (
                    oldValue: AdminDataWorkOpenAccessFundedDTO,
                    newValue: AdminDataWorkOpenAccessFundedDTO
                  ): boolean =>
                    oldValue.affiliation === newValue.affiliation &&
                    oldValue.type === newValue.type
                ),
              },
            }],
          };

          const hasChanges = hasEnrichmentChanges(enrichedValues);

          props.setEnrichedValues((prev: EnrichedValues) =>
            structuredClone({
              ...prev,

              workKeywords: [
                ...((prev.workKeywords as WorkKeywordDTO[] | undefined) || []),
                ...((enrichedValues.workKeywords as WorkKeywordDTO[] | undefined) || [])
                  .filter((newValue: WorkKeywordDTO) =>
                    !((prev.workKeywords as WorkKeywordDTO[] | undefined) || [])
                      .some((oldValue: WorkKeywordDTO) =>
                        oldValue.keyword === newValue.keyword
                      )
                  ),
              ],

              workSubjects: [
                ...((prev.workSubjects as SubjectDTO[] | undefined) || []),
                ...((enrichedValues.workSubjects as SubjectDTO[] | undefined) || [])
                  .filter((newValue: SubjectDTO) =>
                    !((prev.workSubjects as SubjectDTO[] | undefined) || [])
                      .some((oldValue: SubjectDTO) =>
                        oldValue.subjectId === newValue.subjectId &&
                        oldValue.type === newValue.type
                      )
                  ),
              ],

              externalIds: [
                ...((prev.externalIds as ExternalIdDTO[] | undefined) || []),
                ...((enrichedValues.externalIds as ExternalIdDTO[] | undefined) || [])
                  .filter((newValue: ExternalIdDTO) =>
                    !((prev.externalIds as ExternalIdDTO[] | undefined) || [])
                      .some((oldValue: ExternalIdDTO) =>
                        oldValue.externalId === newValue.externalId &&
                        oldValue.type === newValue.type
                      )
                  ),
              ],

              workDescriptions: [
                ...((prev.workDescriptions as WorkDescriptionDTO[] | undefined) || []),
                ...((enrichedValues.workDescriptions as WorkDescriptionDTO[] | undefined) || [])
                  .filter((newValue: WorkDescriptionDTO) =>
                    !((prev.workDescriptions as WorkDescriptionDTO[] | undefined) || [])
                      .some((oldValue: WorkDescriptionDTO) =>
                        oldValue.content === newValue.content &&
                        oldValue.descriptionType === newValue.descriptionType &&
                        oldValue.language === newValue.language
                      )
                  ),
              ],

              adminDataWork:
                (enrichedValues.adminDataWork as AdminDataWorkDTO[] | undefined)?.length
                  ? enrichedValues.adminDataWork
                  : prev.adminDataWork,
            })
          );
          props.setModifiedModel(structuredClone(result));

          toast.notify({
            summary: intl.formatMessage({
              id: hasChanges
                ? 'work.enrichment.success.title'
                : 'work.enrichment.noChanges.title',
            }),
            detail: intl.formatMessage({
              id: hasChanges
                ? 'work.enrichment.success.message'
                : 'work.enrichment.noChanges.message',
            }),
            severity: 'success',
            sticky: false,
            life: 3000,
          });
        })
        .catch((): void => {
          toast.notify({
            severity: 'error',
            summary: intl.formatMessage({id: 'work.enrichment.error.title'}),
            detail: intl.formatMessage({id: 'work.enrichment.error.message'}),
          });
        })
        .finally((): void => {
          setLoading(false);
        });
    };
  };

  return (
    <form className={'h-full w-full'}>
      <Dialog
        header={intl.formatMessage({ id: 'popup.peerReviewed.header' })}
        visible={showPeerReviewedDialog}
        onHide={(): void => setShowPeerReviewedDialog(false)}
      >
        <div className={'flex flex-column gap-2'}>
          <div className={'w-12'}>{intl.formatMessage({ id: 'popup.peerReviewed.text' })}</div>
          <div className={'w-12 flex gap-2'}>
            <Button
              className={'w-4'}
              label={intl.formatMessage({ id: 'popup.peerReviewed.yes' })}
              onClick={(): void => {
                setShowPeerReviewedDialog((): boolean => false);
                props.submit({ peerReviewed: 'true' });
              }}
            />
            <Button
              className={'w-4'}
              label={intl.formatMessage({ id: 'popup.peerReviewed.no' })}
              onClick={(): void => {
                setShowPeerReviewedDialog((): boolean => false);
                props.submit({ peerReviewed: 'false' });
              }}
            />
            <Button
              className={'w-4'}
              label={intl.formatMessage({ id: 'popup.peerReviewed.abort' })}
              severity={'secondary'}
              onClick={(): void => setShowPeerReviewedDialog((): boolean => false)}
            />
          </div>
        </div>
      </Dialog>
      <Layout
        loading={loading}
        loadingOverlay={loading}
        isEditWorkEnrichment
        title={
          props.modifiedModel.id
            ? intl.formatMessage({ id: 'work.header.edit' })
            : intl.formatMessage({ id: 'work.header.create' })
        }
        headerImage={require('../../../Settings/img/banners/edit/work.svg')}
        sidebarWidth={20}
        showSidebarInitially={!props.modifiedModel.pubtype}
        sidebarContent={
          <Block>
            <div className={'col-12 p-fluid'}>
              <LabelledDropdown
                filter={true}
                id={'pubtype'}
                autoFocus={!props.modifiedModel.pubtype}
                appendTo={'self'}
                value={props.modifiedModel.pubtype}
                permissions={{ canRead: true, canWrite: true, required: true }}
                validation={{ valid: !!props.modifiedModel.pubtype }}
                onChange={(pubtype: string): void => props.updateField('pubtype', pubtype)}
                title={'work.attr.pubtype'}
                options={getVocabularyOptions(
                  intl.locale,
                  'pubtype_list',
                  authorizer.hasAnyOfRoles(['ROLE_EDITOR', 'ROLE_ADMIN']) ? 'all' : 'all_user'
                )}
              />
            </div>
            {props.modifiedModel.pubtype && (
              <div className={'col-12'}>
                <Menu id={'work-edit-menu'} pt={{ root: { className: 'w-full' } }} model={menu} />
              </div>
            )}
            {props.modifiedModel.id && props.modifiedModel.externalIds?.length > 0 && (
              <div className={'col-12 mt-3 text-center'}>
                <Button
                  label={intl.formatMessage({ id: 'work.enrichment.button' })}
                  onClick={(e: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => startWorkEnrichment(e)}
                  className='bg-primary text-white'
                />
              </div>
            )}
            {!props.modifiedModel.id && (
              <div className={'col-12 mt-5 flex flex-column gap-3'}>
                <div className={'flex justify-content-center'}>
                  <InternalLink internalIcon={false} to={'/work/import'}>
                    <div className={'flex gap-2 align-items-center'}>
                      <i className={'pi pi-file-import'} />
                      {intl.formatMessage({ id: 'work.link.importByExternalId' })}
                    </div>
                  </InternalLink>
                </div>
                {state.originMandantSettings.mandant === 'rub' && (
                  <div className={'flex justify-content-center'}>
                    <InternalLink internalIcon={false} to={'/work/upload'}>
                      <div className={'flex gap-2 align-items-center'}>
                        <i className={'pi pi-file-arrow-up'} />
                        {intl.formatMessage({ id: 'work.upload.headline' })}
                      </div>
                    </InternalLink>
                  </div>
                )}
                {state.originMandantSettings.mandant === 'hhu' && (
                  <div className={'flex justify-content-center'}>
                    <ExternalLink
                      externalIcon={false}
                      href={`mailto:${intl.formatMessage({ id: 'hhu.report_publist.email' })}?subject=${intl.formatMessage(
                        { id: 'hhu.report_publist.email.subject(givenname, surname)' },
                        {
                          givenname: session.user_givenname,
                          surname: session.user_surname,
                        }
                      )}&body=${intl.formatMessage({ id: 'hhu.report_publist.email.body' })}`}
                    >
                      <div className={'flex gap-2 align-items-center'}>
                        <i className={'pi pi-envelope'} />
                        {intl.formatMessage({ id: 'work.reportPublist' })}
                      </div>
                    </ExternalLink>
                  </div>
                )}
              </div>
            )}
          </Block>
        }
      >
        {props.modifiedModel.pubtype && (
          <FormContent
            onSave={onSubmit}
            saving={props.saving}
            validation={validation}
            isNew={!props.modifiedModel.id}
            next={nextPage ? (event: MouseEvent): void => {
              event.preventDefault();
              props.setPage(nextPage);
            } : null}
            prev={prevPage ? (event: MouseEvent): void => {
              event.preventDefault();
              props.setPage(prevPage);
            } : null}
          >
            {[getPage()]}
          </FormContent>
        )}
        {!props.modifiedModel.pubtype && <></>}
      </Layout>
    </form>
  );
}

export default FormWork;
